import GraphXAxisLabelItem from 'components/atoms/GraphXaxisLabelItem';
import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Legend from 'components/molecules/Legend';
import {
    barWidth,
    legendData,
    salesGraphWidth,
} from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import { yAxisGraphWidth } from 'components/template/CommercialForecast/CFExpandedView';
import { height } from 'components/template/GrowthAnalysis/ExpandedView';
import { UserContext } from 'contexts/ConfigContext';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import React, { FC, useContext, useMemo, useRef } from 'react';
import {
    filterOutDisplayOnlyAttributes,
    formatNumWithKMBAlt,
    getConfig,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import LensConfigManger from '../LensConfigManager/LensConfigManger';
import { LenseConfig } from 'types/pricingSimulator';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { ConfigKeys } from 'utils/Enums';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';

interface ChangeAnalyticsChartItemProps {
    dataItem: any;
    index: number;
    containerWidth: number;
    infScrollerWidthRef: React.MutableRefObject<any[]>;
    infiniteScrollOnNext: (width: number, loading: boolean, index: number) => void;
    indexLoaderErrorMapping: any;
    selectedLensItem: any[];
    selectedReportInfo: any;
    updateReportProperty: Function;
    mainHeader: string;
    enableOneChangeAnalyticsChart: boolean;
}

const ChangeAnalyticsChartItem: FC<ChangeAnalyticsChartItemProps> = ({
    dataItem,
    index,
    containerWidth,
    infScrollerWidthRef,
    infiniteScrollOnNext,
    indexLoaderErrorMapping,
    selectedLensItem,
    selectedReportInfo,
    updateReportProperty,
    mainHeader,
    enableOneChangeAnalyticsChart,
}) => {
    const { appConfig } = useContext(UserContext);
    const { showPercentageChange } = useContext(PricingAnalyticsDataContext);
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;

    const chartRef = useRef(null);

    const graphViewContainerWidth = Math.max(
        dataItem?.data ? dataItem.data?.length * salesGraphWidth : 0,
        enableOneChangeAnalyticsChart ? containerWidth : containerWidth / 2,
    );

    const perGraphComponentWidth = graphViewContainerWidth / dataItem?.data?.length || 0;

    const excelData = useMemo(() => {
        return dataItem?.data?.map((item: any) => [item?.category, item?.sales]);
    }, [dataItem]);

    return (
        <div key={dataItem?.id} className="p-4 border border-backgroundGreyAlt1 rounded-lg" ref={chartRef}>
            {indexLoaderErrorMapping?.[index]?.[0] ? (
                <div style={{ height }}>
                    <Loader />
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center">
                        <p className="text-subHeaderColor font-sm font-medium">{mainHeader}</p>
                        <div className="flex gap-2 items-center">
                            <LensConfigManger
                                placeHolderLabel="Search KPI..."
                                showAttributePanelToTheRight={enableOneChangeAnalyticsChart ? false : index === 0}
                                buttonName={selectedLensItem?.[index]?.name}
                                initialConfigDetails={
                                    filterOutDisplayOnlyAttributes(
                                        selectedReportInfo?.changeAnalytics?.input?.[index]?.lensConfig || [],
                                    ) || []
                                }
                                onSubmitHandler={(updatedDetails: LenseConfig[]) => {
                                    let kpiId: any = null;
                                    updatedDetails?.forEach((lens) => {
                                        lens?.attributes?.forEach((kpi) => {
                                            if (kpi?.isSelected) {
                                                kpiId = kpi?.attributeId;
                                                return;
                                            }
                                            if (kpiId) return;
                                        });
                                        if (kpiId) return;
                                    });
                                    //update lensconfig and kpi id
                                    const tempInput = selectedReportInfo?.changeAnalytics?.input?.map(
                                        (graphItem: any, graphIndex: number) => {
                                            const isGraphItem = graphIndex === index;
                                            return {
                                                ...graphItem,
                                                kpiId: isGraphItem ? kpiId : graphItem?.kpiId,
                                                lensConfig: isGraphItem ? updatedDetails : graphItem?.lensConfig,
                                            };
                                        },
                                    );
                                    updateReportProperty('changeAnalytics', {
                                        output: selectedReportInfo?.changeAnalytics?.output,
                                        input: tempInput,
                                    });
                                }}
                                isMultiSelect={false}
                                outputCallLoading={false}
                                isLoading={false}
                                saveButtonLabel="Save Changes"
                                addAlignmentFix={index === 1}
                                scrollContainerMaxHeight={250}
                            />
                            <span className="flex items-center gap-2">
                                <CopyToClipBoard refOfComponent={chartRef} chartOnly={true} />
                                <DownloadExcel
                                    title={mainHeader}
                                    excelData={excelData}
                                    headers={[
                                        selectedReportInfo?.changeAnalytics?.input?.[index]?.attributeType?.label,
                                        selectedLensItem?.[index]?.name,
                                    ]}
                                    excludeFilters
                                />
                            </span>
                        </div>
                    </div>
                    {indexLoaderErrorMapping?.[index]?.[1] ? (
                        <div className="flex items-center justify-center" style={{ height: `calc(100% - 100px)` }}>
                            <ErrorBox msg={String(indexLoaderErrorMapping?.[index]?.[1])} />
                        </div>
                    ) : (
                        <>
                            {/* LEGEND WRAPPER */}
                            <div className="flex justify-between my-4">
                                <p className="w-36 font-xs text-lightBlack">{selectedLensItem?.[index]?.name}</p>
                                <Legend data={legendData} />
                                <p className="w-22"></p>
                            </div>
                            <div id={`change-analytics-${dataItem.id}`} className="flex ml-1 w-full">
                                {/* Y AXIS */}
                                <div style={{ minWidth: yAxisGraphWidth }}>
                                    <Barchart
                                        colorKey="color"
                                        dataset={[]}
                                        height={height}
                                        loading={false}
                                        chartContainer={`SalesY-${dataItem.id}`}
                                        fields={['category', 'sales']}
                                        minBound={scaleMinValue(80, dataItem?.minVal)}
                                        maxBound={scaleMaxValue(80, dataItem?.maxVal)}
                                        width={yAxisGraphWidth}
                                        YGrid={false}
                                        padding={[0, 0, 0, 0]}
                                        yAxisLabelFormatter={(val: any) => {
                                            return formatNumWithKMBAlt(
                                                val,
                                                showPercentageChange ? decimalForPercentage : decimalForNonPercentage,
                                                showPercentageChange ? decimalForPercentage : decimalForNonPercentage,
                                            );
                                        }}
                                        xAxisConfig={false}
                                    />
                                </div>
                                {/* MAIN AXIS */}
                                <HorizontalInfiniteScroller
                                    id={`change-analytics-infinite-scroll${dataItem.id}`}
                                    hasMore={dataItem?.data?.length < dataItem.count || false}
                                    scrollThreshold={0.9}
                                    toScrollWidth={infScrollerWidthRef.current?.[index] ?? 0}
                                    onNext={(width: number) => {
                                        infiniteScrollOnNext(width, indexLoaderErrorMapping?.[index]?.[0], index);
                                    }}
                                >
                                    <div
                                        id={`change-analytics-infinite-scroll${dataItem.id}`}
                                        className="flex pb-[45px]"
                                        style={{ maxWidth: '100%', overflowX: 'auto' }}
                                    >
                                        <div>
                                            <div style={{ height: height }}>
                                                <Barchart
                                                    barWidth={barWidth}
                                                    dataset={dataItem?.data || []}
                                                    height={height}
                                                    loading={false}
                                                    chartContainer={`SalesX-${dataItem.id}`}
                                                    fields={['category', 'sales']}
                                                    intervalLabelTooltipKey={'labelSales'}
                                                    minBound={scaleMinValue(80, dataItem?.minVal)}
                                                    maxBound={scaleMaxValue(80, dataItem?.maxVal)}
                                                    width={graphViewContainerWidth || salesGraphWidth}
                                                    intervalFormatter={(val) => {
                                                        return `${
                                                            showPercentageChange
                                                                ? ''
                                                                : selectedLensItem?.[index]?.pre ?? ''
                                                        }${formatNumWithKMBAlt(
                                                            val,
                                                            showPercentageChange
                                                                ? decimalForPercentage
                                                                : decimalForNonPercentage,
                                                            showPercentageChange
                                                                ? decimalForPercentage
                                                                : decimalForNonPercentage,
                                                        )}${showPercentageChange ? '%' : ''}`;
                                                    }}
                                                    tooltipFormatter={(xAxis, val) => {
                                                        const tooltipVal = `${
                                                            showPercentageChange
                                                                ? ''
                                                                : selectedLensItem?.[index]?.pre ?? ''
                                                        }${formatNumWithKMBAlt(
                                                            val,
                                                            showPercentageChange
                                                                ? decimalForPercentage
                                                                : decimalForNonPercentage,
                                                            showPercentageChange
                                                                ? decimalForPercentage
                                                                : decimalForNonPercentage,
                                                        )}${showPercentageChange ? '%' : ''}`;
                                                        return {
                                                            name: xAxis,
                                                            value: tooltipVal,
                                                        };
                                                    }}
                                                    colorKey={'color'}
                                                />
                                            </div>
                                            {/* X AXIS ITEM LABEL */}
                                            <div className="flex items-center">
                                                {dataItem?.data?.map((item: any) => {
                                                    return (
                                                        <GraphXAxisLabelItem
                                                            key={item.id || item.category}
                                                            width={perGraphComponentWidth || salesGraphWidth}
                                                            item={item}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </HorizontalInfiniteScroller>
                            </div>
                            <p className="text-lightBlack font-xs relative top-[-40px] left-[25px] flex justify-center">
                                {selectedReportInfo?.changeAnalytics?.input?.[index]?.attributeType?.label}
                            </p>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ChangeAnalyticsChartItem;
